// =============================================================================
// Configurações → Integrações
//
// De onde o sistema lê o dado, se a leitura está em dia, e — para quem é admin —
// como reconectar o Conta Azul sem depender de desenvolvedor.
//
// ⚠️ Nenhum segredo aparece aqui, em nenhuma tela e em nenhum estado de erro. O
// que se mostra da credencial é o TAMANHO em bytes, a versão da chave e o
// carimbo da última rotação (§5.4). O Client Secret só trafega no sentido
// navegador → servidor, e só quando a pessoa está trocando a aplicação.
//
// O fluxo de reconexão é de dois passos porque o `redirect_uri` registrado na
// aplicação do Conta Azul é a home deles: o código de autorização volta na
// barra de endereço e precisa ser trazido de volta à mão. É a mesma limitação
// que o script `autorizar-conta-azul.mjs` já contornava assim.
// =============================================================================

import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, FalhaApi } from "../lib/api.ts";
import { Modal } from "../componentes/Modal.tsx";
import { Painel, Pilula, Vazio } from "../componentes/ui.tsx";
import type { Tom } from "../lib/formato.ts";

type Credencial = {
  versao_chave: string;
  algoritmo: string;
  bytes: number;
  criado_em: string;
  atualizado_em: string;
  rotacionado_em: string | null;
};

type Execucao = {
  momento: string;
  rota: string;
  competencia: string | null;
  status_http: number | null;
  completo: boolean;
  duracao_ms: number | null;
};

type Resposta = {
  conta_azul: {
    pode_gerenciar: boolean;
    pode_reprocessar: boolean;
    reprocessamento: {
      id: string;
      estado: "executando" | "concluido" | "falhou";
      iniciado_em: string;
      terminado_em: string | null;
      mensagem: string;
    } | null;
    credencial: Credencial | null;
    sincronizacao: {
      ultima_sincronizacao: string | null;
      ultima_competencia_completa: string | null;
      registros: string | null;
      status: string;
      detalhe: string;
    } | null;
    cadastros: { momento: string; modo: string | null } | null;
    execucoes: Execucao[];
    redirect_uri: string;
  };
  enotas: { configurada: boolean };
};

const DATA_HORA_SP = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

function quando(iso: string | null | undefined): string {
  return iso ? DATA_HORA_SP.format(new Date(iso)) : "—";
}

const TOM_STATUS_SYNC: Record<string, Tom> = {
  ok: "ok",
  atrasado: "atencao",
  falha: "risco",
};
const ROTULO_STATUS_SYNC: Record<string, string> = {
  ok: "Sincronização em dia",
  atrasado: "Atrasada",
  falha: "Com falha",
};

export function Integracoes() {
  const [reconectando, setReconectando] = useState(false);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["integracoes"],
    queryFn: () => api.get<Resposta>("/api/integracoes"),
    refetchInterval: (consulta) =>
      consulta.state.data?.conta_azul.reprocessamento?.estado === "executando" ? 2_000 : false,
  });
  const reprocessar = useMutation({
    mutationFn: (corpo: { modo?: "recalcular"; de?: string; ate?: string }) =>
      api.post("/api/integracoes/conta-azul/reprocessar", corpo),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["integracoes"] }),
  });

  // O recálculo por período começa em janeiro do ano corrente e vai até o mês
  // corrente: é o caso de depois que o Financeiro completa o orçamento, e o
  // botão de três meses não alcança o começo do ano.
  const hoje = new Date();
  const mesAtual = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`;
  const [recalcDe, setRecalcDe] = useState(`${hoje.getFullYear()}-01`);
  const [recalcAte, setRecalcAte] = useState(mesAtual);

  if (q.isError) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16">
        <Vazio
          titulo="Não foi possível carregar as integrações"
          dica={(q.error as FalhaApi)?.message ?? "Tente recarregar a página."}
        />
      </div>
    );
  }

  const ca = q.data?.conta_azul;
  const status = ca?.sincronizacao?.status ?? null;

  return (
    <div className="pagina pb-20">
      <header className="py-7">
        <p className="text-[11px] font-semibold tracking-[0.11em] text-tinta-fraca uppercase">
          Configurações
        </p>
        <h1 className="mt-1 font-display text-[22px] leading-tight font-bold tracking-tight">
          Integrações
        </h1>
        <p className="mt-0.5 max-w-2xl text-[13.5px] text-tinta-media">
          De onde o sistema lê o realizado, o plano de contas e os centros de custo — e se essa
          leitura está em dia.
        </p>
      </header>

      <Painel>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2.5">
              <h2 className="font-display text-[17px] font-bold tracking-tight">Conta Azul</h2>
              {status && (
                <Pilula tom={TOM_STATUS_SYNC[status] ?? "neutro"}>
                  {ROTULO_STATUS_SYNC[status] ?? status}
                </Pilula>
              )}
            </div>
            <p className="mt-1 max-w-2xl text-[13px] text-tinta-media">
              Fonte oficial do realizado, do plano de contas e dos centros de custo. O sistema{" "}
              <strong className="font-semibold">lê</strong>; lançamento contábil continua sendo lá.
            </p>
          </div>

          <div className="flex shrink-0 flex-wrap gap-2">
            {ca?.pode_reprocessar && (
              <button
                type="button"
                disabled={reprocessar.isPending || ca.reprocessamento?.estado === "executando"}
                onClick={() => reprocessar.mutate({})}
                className="rounded-lg border border-linha bg-superficie px-4 py-2 text-[13px] font-semibold text-azul hover:border-azul disabled:cursor-not-allowed disabled:opacity-50"
              >
                {ca.reprocessamento?.estado === "executando"
                  ? "Reprocessando…"
                  : "Reprocessar (3 meses e os à frente)"}
              </button>
            )}
            {ca?.pode_gerenciar && (
              <button
                type="button"
                onClick={() => setReconectando(true)}
                className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul"
              >
                Reconectar
              </button>
            )}
          </div>
        </div>

        {q.isPending ? (
          <div className="mt-5 h-24 animate-pulse rounded-lg bg-superficie-2" />
        ) : (
          <>
            <dl className="mt-5 grid gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
              <Linha rotulo="Última leitura do realizado">
                {quando(ca?.sincronizacao?.ultima_sincronizacao)}
              </Linha>
              <Linha rotulo="Última competência completa">
                {ca?.sincronizacao?.ultima_competencia_completa
                  ? new Date(`${ca.sincronizacao.ultima_competencia_completa}T00:00:00`)
                      .toLocaleDateString("pt-BR", { month: "long", year: "numeric" })
                  : "—"}
              </Linha>
              <Linha rotulo="Cadastros atualizados em">
                {quando(ca?.cadastros?.momento)}
                {ca?.cadastros ? (ca.cadastros.modo === "manual" ? " (pelo botão)" : " (automática)") : ""}
              </Linha>
            </dl>

            {ca?.sincronizacao && ca.sincronizacao.status !== "ok" && (
              <p className="mt-4 rounded-lg border border-atencao-linha bg-atencao-bg px-3 py-2 text-[12.5px] text-atencao-tinta">
                {ca.sincronizacao.detalhe}
                {ca.sincronizacao.status === "falha" && (
                  <>
                    {" "}
                    Se as falhas persistirem, o mais provável é o <code>refresh_token</code> ter sido
                    invalidado — e aí o conserto é reconectar, com uma conta de administrador.
                  </>
                )}
              </p>
            )}

            {ca?.pode_reprocessar && (
              <div className="mt-5 rounded-lg border border-linha px-3 py-3">
                <p className="text-[13px] font-semibold">Recalcular um período</p>
                <p className="mt-0.5 max-w-3xl text-[12.5px] text-tinta-media">
                  Refaz o casamento entre orçamento e realizado com o que já está no sistema, sem ler
                  o Conta Azul. Use depois de completar o orçamento ou corrigir centros de custo:
                  meses mais antigos que os três últimos só se atualizam por aqui.
                </p>
                <div className="mt-2.5 flex flex-wrap items-end gap-3">
                  <label className="text-[12px] text-tinta-media">
                    De
                    <input
                      type="month"
                      value={recalcDe}
                      max={recalcAte}
                      onChange={(e) => setRecalcDe(e.target.value)}
                      className="mt-1 block rounded-lg border border-linha bg-superficie px-3 py-1.5 text-[13px] text-tinta"
                    />
                  </label>
                  <label className="text-[12px] text-tinta-media">
                    Até
                    <input
                      type="month"
                      value={recalcAte}
                      min={recalcDe}
                      onChange={(e) => setRecalcAte(e.target.value)}
                      className="mt-1 block rounded-lg border border-linha bg-superficie px-3 py-1.5 text-[13px] text-tinta"
                    />
                  </label>
                  <button
                    type="button"
                    disabled={
                      !recalcDe || !recalcAte || reprocessar.isPending
                      || ca.reprocessamento?.estado === "executando"
                    }
                    onClick={() => reprocessar.mutate({ modo: "recalcular", de: recalcDe, ate: recalcAte })}
                    className="rounded-lg border border-linha bg-superficie px-4 py-2 text-[13px] font-semibold text-azul hover:border-azul disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Recalcular período
                  </button>
                </div>
              </div>
            )}

            {reprocessar.isError && (
              <p className="mt-4 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
                {(reprocessar.error as FalhaApi).message}
              </p>
            )}

            {ca?.reprocessamento && (
              <div className={`mt-4 rounded-lg border px-3 py-2.5 text-[12.5px] ${
                ca.reprocessamento.estado === "falhou"
                  ? "border-risco-linha bg-risco-bg text-risco-tinta"
                  : ca.reprocessamento.estado === "concluido"
                    ? "border-ok-linha bg-ok-bg text-ok-tinta"
                    : "border-atencao-linha bg-atencao-bg text-atencao-tinta"
              }`}>
                <p className="font-semibold">
                  {ca.reprocessamento.estado === "executando"
                    ? "Reprocessamento em andamento"
                    : ca.reprocessamento.estado === "concluido"
                      ? "Reprocessamento concluído"
                      : "Reprocessamento falhou"}
                </p>
                <p className="mt-0.5 break-words">{ca.reprocessamento.mensagem}</p>
                <p className="num mt-1 text-[11.5px] opacity-80">
                  Iniciado em {quando(ca.reprocessamento.iniciado_em)}
                  {ca.reprocessamento.terminado_em
                    ? ` · terminado em ${quando(ca.reprocessamento.terminado_em)}`
                    : ""}
                </p>
              </div>
            )}

            {/* A credencial só é legível para admin (§5.3). Para o financeiro, a
                ausência deste bloco não significa que não há credencial: o sinal
                de que a integração funciona é a sincronização, acima. */}
            {ca?.pode_gerenciar && (
              <div className="mt-5 rounded-lg border border-linha bg-superficie-2/60 px-4 py-3">
                <p className="text-[11px] font-semibold tracking-wide text-tinta-fraca uppercase">
                  Credencial
                </p>
                {ca.credencial ? (
                  <>
                    <dl className="mt-2 grid gap-x-8 gap-y-2 sm:grid-cols-2 lg:grid-cols-4">
                      <Linha rotulo="Última rotação do token">
                        {quando(ca.credencial.rotacionado_em)}
                      </Linha>
                      <Linha rotulo="Autorizada em">{quando(ca.credencial.criado_em)}</Linha>
                      <Linha rotulo="Cifra">
                        {ca.credencial.algoritmo} · chave {ca.credencial.versao_chave}
                      </Linha>
                      <Linha rotulo="Tamanho cifrado">{ca.credencial.bytes} bytes</Linha>
                    </dl>
                    <p className="mt-2.5 text-[12px] leading-snug text-tinta-fraca">
                      O segredo é cifrado pela aplicação e o banco nunca vê a chave — um dump não
                      entrega nada utilizável. Por isso aqui só aparecem tamanho e carimbo, nunca o
                      valor. Se a <strong>última rotação</strong> parar de andar enquanto há
                      sincronização acontecendo, é sinal de que alguma outra coisa está autenticando
                      sem gravar o token de volta.
                    </p>
                  </>
                ) : (
                  <p className="mt-1.5 text-[13px] text-tinta-media">
                    Nenhuma credencial gravada. Enquanto não houver, a sincronização não roda —
                    clique em <strong>Reconectar</strong> e autorize com um administrador da conta
                    do Conta Azul.
                  </p>
                )}
              </div>
            )}

            {ca && ca.execucoes.length > 0 && <Execucoes execucoes={ca.execucoes} />}
          </>
        )}
      </Painel>

      <Painel className="mt-4">
        <div className="flex flex-wrap items-center gap-2.5">
          <h2 className="font-display text-[17px] font-bold tracking-tight">eNotas</h2>
          <Pilula tom="neutro">Não configurada</Pilula>
        </div>
        <p className="mt-1.5 max-w-2xl text-[13px] text-tinta-media">
          Emissão e acompanhamento fiscal. Está prevista no PRD §10 e ainda não foi construída — a
          emissão hoje roda pelo fluxo Make → eNotas, fora deste sistema. Não há nada a configurar
          aqui até a frente entrar.
        </p>
      </Painel>

      {reconectando && ca && (
        <Reconectar
          redirectUri={ca.redirect_uri}
          temCredencial={!!ca.credencial}
          onFechar={() => setReconectando(false)}
        />
      )}
    </div>
  );
}

function Linha({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold tracking-wide text-tinta-fraca uppercase">{rotulo}</dt>
      <dd className="num mt-0.5 text-[13.5px] text-tinta">{children}</dd>
    </div>
  );
}

function Execucoes({ execucoes }: { execucoes: Execucao[] }) {
  const [aberto, setAberto] = useState(false);
  return (
    <div className="mt-4">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-expanded={aberto}
        className="text-[12.5px] font-semibold text-azul-medio hover:underline"
      >
        {aberto ? "Ocultar" : "Ver"} as últimas chamadas ao Conta Azul
      </button>
      {aberto && (
        <div className="thin-scroll mt-2 overflow-x-auto">
          <table className="w-full min-w-[540px] text-[12.5px]">
            <thead>
              <tr className="border-b border-linha text-left text-[11px] tracking-wide text-tinta-fraca uppercase">
                <th className="py-1.5 font-semibold">Quando</th>
                <th className="py-1.5 font-semibold">Rota</th>
                <th className="py-1.5 font-semibold">Competência</th>
                <th className="py-1.5 text-right font-semibold">HTTP</th>
                <th className="py-1.5 text-right font-semibold">Duração</th>
                <th className="py-1.5 text-right font-semibold">Completa</th>
              </tr>
            </thead>
            <tbody>
              {execucoes.map((e, i) => (
                <tr key={`${e.momento}-${i}`} className="border-b border-linha/60">
                  <td className="num py-1.5">{quando(e.momento)}</td>
                  <td className="py-1.5 text-tinta-media">{e.rota}</td>
                  <td className="num py-1.5">{e.competencia?.slice(0, 7) ?? "—"}</td>
                  <td
                    className={`num py-1.5 text-right ${
                      (e.status_http ?? 0) >= 400 ? "font-semibold text-risco-tinta" : ""
                    }`}
                  >
                    {e.status_http ?? "—"}
                  </td>
                  <td className="num py-1.5 text-right">{e.duracao_ms ?? "—"} ms</td>
                  <td className="py-1.5 text-right">{e.completo ? "sim" : "não"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── reconexão ───────────────────────────────────────────────────────────────

/**
 * Dois passos, e o segundo depende de a pessoa sair daqui e voltar.
 *
 * O `code` do OAuth expira em minutos e vale uma vez só — por isso o passo 2
 * não pode ser automático "mais tarde", e por isso o erro mais comum é código
 * vencido. A mensagem de recusa diz exatamente isso, em vez de "invalid_grant".
 */
function Reconectar({
  redirectUri,
  temCredencial,
  onFechar,
}: {
  redirectUri: string;
  temCredencial: boolean;
  onFechar: () => void;
}) {
  const qc = useQueryClient();
  const [trocarApp, setTrocarApp] = useState(!temCredencial);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [retorno, setRetorno] = useState("");
  const [pronto, setPronto] = useState(false);

  const autorizar = useMutation({
    mutationFn: () =>
      api.post<{ id: string; url: string; redirect_uri: string; expira_em_s: number }>(
        "/api/integracoes/conta-azul/autorizar",
        trocarApp ? { client_id: clientId, client_secret: clientSecret } : {},
      ),
    onSuccess: (r) => {
      // O segredo digitado não precisa continuar na memória do navegador depois
      // de chegar ao servidor.
      setClientSecret("");
      window.open(r.url, "_blank", "noopener");
    },
  });

  const concluir = useMutation({
    mutationFn: () =>
      api.post<{ credencial: Credencial | null }>("/api/integracoes/conta-azul/concluir", {
        id: autorizar.data?.id,
        retorno,
      }),
    onSuccess: async () => {
      setRetorno("");
      setPronto(true);
      await qc.invalidateQueries();
    },
  });

  return (
    <Modal titulo="Reconectar o Conta Azul" onFechar={onFechar} largura="max-w-xl">
      {pronto ? (
        <div>
          <p className="rounded-lg border border-ok-linha bg-ok-bg px-3 py-2.5 text-[13px] text-ok-tinta">
            Credencial gravada e cifrada. A sincronização da próxima madrugada já usa a autorização
            nova — e cada renovação grava o token rotacionado de volta, sozinha.
          </p>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={onFechar}
              className="rounded-lg bg-azul px-4 py-2 text-[13px] font-bold text-white"
            >
              Fechar
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <p className="text-[13px] leading-snug text-tinta-media">
            Reconectar é o conserto de quando o token morre e o Realizado para de atualizar. Você
            precisa de uma conta <strong>administradora do Conta Azul</strong> para autorizar.
          </p>

          {/* ── passo 1 ── */}
          <section>
            <h3 className="font-display text-[14px] font-bold">1. Autorizar no Conta Azul</h3>

            {temCredencial && (
              <label className="mt-2 flex items-start gap-2 text-[12.5px] text-tinta-media">
                <input
                  type="checkbox"
                  checked={trocarApp}
                  onChange={(e) => setTrocarApp(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  Trocar também o Client ID e o Client Secret (só se a aplicação do Conta Azul mudou;
                  para renovar o acesso da mesma aplicação, deixe desmarcado).
                </span>
              </label>
            )}

            {trocarApp && (
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="text-[11px] font-semibold tracking-wide text-tinta-fraca uppercase">
                    Client ID
                  </span>
                  <input
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    autoComplete="off"
                    className="mt-1 w-full rounded-lg border border-linha px-2.5 py-1.5 text-[13px]"
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] font-semibold tracking-wide text-tinta-fraca uppercase">
                    Client Secret
                  </span>
                  <input
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    type="password"
                    autoComplete="off"
                    className="mt-1 w-full rounded-lg border border-linha px-2.5 py-1.5 text-[13px]"
                  />
                </label>
              </div>
            )}

            <button
              type="button"
              disabled={autorizar.isPending || (trocarApp && (!clientId.trim() || !clientSecret.trim()))}
              onClick={() => autorizar.mutate()}
              className="mt-3 rounded-lg bg-azul px-4 py-2 text-[13px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-45"
            >
              {autorizar.isPending
                ? "Gerando…"
                : autorizar.data
                  ? "Gerar outro link"
                  : "Abrir a autorização"}
            </button>

            {autorizar.isError && (
              <p className="mt-2 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
                {(autorizar.error as FalhaApi).message}
              </p>
            )}

            {autorizar.data && (
              <p className="mt-2 text-[12.5px] leading-snug text-tinta-media">
                Abriu numa aba nova. Se o navegador bloqueou,{" "}
                <a
                  href={autorizar.data.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-azul-medio underline"
                >
                  abra por aqui
                </a>
                . Depois de autorizar, o Conta Azul devolve você para{" "}
                <span className="num">{redirectUri}</span> com um código na barra de endereço.
              </p>
            )}
          </section>

          {/* ── passo 2 ── */}
          <section className={autorizar.data ? "" : "opacity-45"}>
            <h3 className="font-display text-[14px] font-bold">2. Trazer o retorno de volta</h3>
            <p className="mt-1 text-[12.5px] leading-snug text-tinta-media">
              Copie a barra de endereço inteira da página para onde o Conta Azul te levou e cole
              abaixo. O código vale uma vez e expira em poucos minutos.
            </p>
            <textarea
              value={retorno}
              onChange={(e) => setRetorno(e.target.value)}
              disabled={!autorizar.data}
              rows={3}
              spellCheck={false}
              placeholder={`${redirectUri}/?code=...&state=...`}
              className="mt-2 w-full rounded-lg border border-linha px-2.5 py-1.5 font-mono text-[12px] break-all disabled:bg-superficie-2"
            />

            {concluir.isError && (
              <p className="mt-2 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
                {(concluir.error as FalhaApi).message}
              </p>
            )}

            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={onFechar}
                className="rounded-lg border border-linha px-4 py-2 text-[13px] font-semibold text-tinta-media"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={!autorizar.data || !retorno.trim() || concluir.isPending}
                onClick={() => concluir.mutate()}
                className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45"
              >
                {concluir.isPending ? "Gravando…" : "Concluir a reconexão"}
              </button>
            </div>
          </section>
        </div>
      )}
    </Modal>
  );
}
